import type { GameInput, InputRejection, RuleEvent, RuleSet, Transition } from '../rules/contracts';
import type {
  GameModeId,
  Json,
  MatchEndReason,
  MatchId,
  MatchResult,
  PlayerId,
  SeatIndex,
  Timestamp,
} from '../types';

/**
 * A GameMode is the orchestration around a RuleSet: how many seats, who may act
 * when, what "finished" means across seats, and how a finished match turns into
 * a persisted result.
 *
 * The split that matters:
 *
 *   RuleSet  - pure, stateless, decides what a move does.
 *   GameMode - stateful for the length of one match, decides what a match *is*.
 *
 * Everything else in the system (the room, the renderer, the repositories)
 * talks to this interface and never to a concrete ruleset.
 */

export interface ModeSeat {
  readonly seat: SeatIndex;
  /** Null for an unclaimed seat or a guest. */
  readonly playerId: PlayerId | null;
  readonly displayName: string;
}

export interface ModeSetup {
  readonly matchId: MatchId;
  /** Server-generated. Never accepted from a client. */
  readonly seed: string;
  readonly seats: readonly ModeSeat[];
  /** When `playing` begins, on the server clock. */
  readonly startedAt: Timestamp;
  /** Mode-specific configuration, forwarded to the ruleset as `RuleSetup.config`. */
  readonly config?: Json;
  /**
   * False on the predicting client, true on the authoritative server. A mode
   * uses it to skip work that only the server may do (persistence, rating).
   */
  readonly authoritative: boolean;
}

/** What one step produced. The room broadcasts it; the renderer animates it. */
export interface ModeStep {
  /** Seat that caused this step. Present for input and autonomous bot steps. */
  readonly seat?: SeatIndex;
  readonly transitions: readonly Transition[];
  readonly events: readonly RuleEvent[];
  /** Set when the step was refused. Nothing changed. */
  readonly rejected?: InputRejection;
  /** True when this step ended the match. */
  readonly finished: boolean;
}

export const EMPTY_STEP: ModeStep = Object.freeze({
  transitions: Object.freeze([]) as readonly Transition[],
  events: Object.freeze([]) as readonly RuleEvent[],
  finished: false,
});

export interface GameMode {
  readonly id: GameModeId;
  /** Which ruleset this instance runs. Resolved through the registry, by id. */
  readonly ruleSetId: string;
  /** Seats that must be filled before the match can start. */
  readonly requiredSeats: number;
  /** Null means the server does not schedule a round timeout. */
  readonly roundDurationMs: number | null;

  /** 1. Initialise state. Called once, when the match enters `playing`. */
  init(setup: ModeSetup): void;

  /** 2. Process one player input. */
  handleInput(input: GameInput, now: Timestamp): ModeStep;

  /**
   * Time-driven progress, for modes whose rules have any. Returns null when
   * nothing happened, so the server broadcasts nothing.
   */
  tick(now: Timestamp): ModeStep | null;

  /** 3. Is the match over? */
  isFinished(): boolean;

  /** Server-only terminal path: timeout, surrender or lost reconnection. */
  forceFinish(reason: MatchEndReason, losingSeat?: SeatIndex): void;

  /** 4. The result. Only valid once `isFinished()` is true. */
  result(endedAt: Timestamp, reason: MatchEndReason): MatchResult;

  /**
   * Full state, for a joining spectator, a reconnecting player, or the match
   * log. Must round-trip through `restore`.
   */
  snapshot(): Json;
  /** Rules-only portion used to mirror authoritative state into Colyseus schema. */
  stateSnapshot(): Json;
  restore(snapshot: Json): void;
}

/**
 * How the registry builds one. Kept separate from `GameMode` so a mode can be
 * described (seat count, label) without instantiating a match.
 */
export interface GameModeDefinition {
  readonly id: GameModeId;
  readonly requiredSeats: number;
  /** Which ruleset the mode defaults to. Overridable per match. */
  readonly defaultRuleSetId: string;
  create(ruleSetId?: string): GameMode;
}

/** Convenience alias for code holding a ruleset whose state type it does not know. */
export type ErasedRuleSet = RuleSet<unknown, unknown>;
